import { useState, lazy, Suspense, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { TextField } from "@/components/TextField";
import { SubmitButton } from "@/components/SubmitButton";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import { useAuth } from "@/auth/AuthProvider";
import {
  beginAuth,
  login,
  setPassword,
  completeMfa,
  type BrokerAuthMethod,
  type LoginChallenge,
} from "@/lib/brokerAuth";
import { EmailOtpLogin } from "@/components/EmailOtpLogin";
import {
  signInWithPasskey,
  isPasskeySupported,
  describePasskeyError,
} from "@/lib/passkeys";
import { ApiError } from "@/lib/api";

// Capability-adaptive broker sign-in (BRK-S017, paired with PMS-S048).
//   1. email   — collect the email, then POST /api/broker/auth/begin to learn
//      which methods the account can use (no dead buttons).
//   2. method  — render ONLY the returned methods: password (+ its
//      NEW_PASSWORD_REQUIRED / SOFTWARE_TOKEN_MFA challenges), "email me a code"
//      (email OTP), and/or "use a passkey".
// Every path ends the same way: tokens -> completeLogin() resolves /me and the
// broker lands where they were headed. Errors carry method-specific wording from
// the PMS BFF, shown once in the method step rather than always under password.

// Dev-only "Dev login" affordance (BRK-S037). Gating the lazy() on build-time
// constants means a build that opts into NEITHER MODE=development nor
// VITE_ENABLE_DEV_LOGIN=true folds this to `null` and Rollup drops the dev chunk
// entirely — nothing ships to prod. A deployed non-prod env (sandbox1) opts in
// via VITE_ENABLE_DEV_LOGIN. Under Vitest (MODE="test", flag unset) it's null,
// so LoginPage tests are unaffected.
const DevLoginButton =
  import.meta.env.MODE === "development" ||
  import.meta.env.VITE_ENABLE_DEV_LOGIN === "true"
    ? lazy(() => import("@/dev/DevLoginButton"))
    : null;

type LocationState = { from?: string; notice?: string } | null;

type Phase = "email" | "method" | "email-otp";

function messageFor(err: unknown): string {
  // PMS returns a client-safe message in its { error } envelope; surface it.
  if (err instanceof ApiError) return err.message;
  return "Something went wrong. Please try again.";
}

// Prominent secondary action for the alternative sign-in methods — same footprint
// as the primary SubmitButton, outlined rather than filled (BRK-S018).
const altMethodClass =
  "flex w-full items-center justify-center gap-2 rounded-md border border-brk-accent px-5 py-3 font-medium text-brk-accent transition-colors hover:bg-brk-bg disabled:cursor-not-allowed disabled:opacity-60";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeLogin } = useAuth();
  const locationState = location.state as LocationState;
  const destination = locationState?.from ?? "/portal";
  const notice = locationState?.notice;

  const [phase, setPhase] = useState<Phase>("email");
  const [email, setEmail] = useState("");
  const [methods, setMethods] = useState<BrokerAuthMethod[]>([]);
  // When email-OTP is the only method, /begin already emailed a code + gave us a
  // session — reuse it (BRK-S025) instead of sending a second one.
  const [beginSession, setBeginSession] = useState<string | undefined>(
    undefined,
  );
  const [emailOtpPreSent, setEmailOtpPreSent] = useState(false);
  const [password, setPasswordValue] = useState("");
  const [challenge, setChallenge] = useState<LoginChallenge | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const passkeySupported = isPasskeySupported();

  async function finish(tokensPromise: Promise<void>) {
    await tokensPromise;
    navigate(destination, { replace: true });
  }

  // Step 1: probe the account's available methods, then show only those. Passkey
  // is dropped if the browser can't run the ceremony.
  async function onSubmitEmail(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const res = await beginAuth(email);
      const available = res.methods.filter(
        (m) => m !== "passkey" || passkeySupported,
      );
      setMethods(available);
      // Email-OTP is the only way in → skip the method picker and go straight to
      // code entry. /begin already emailed the code and gave us the session, so
      // reuse it rather than sending a second (BRK-S025).
      if (available.length === 1 && available[0] === "email_otp") {
        setBeginSession(res.session);
        setEmailOtpPreSent(Boolean(res.emailOtpSent && res.session));
        setPhase("email-otp");
      } else {
        setPhase("method");
      }
    } catch (err) {
      // /begin only rejects on a genuine outage (it's non-enumerating).
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  function useDifferentEmail() {
    setPhase("email");
    setMethods([]);
    setBeginSession(undefined);
    setEmailOtpPreSent(false);
    setPasswordValue("");
    setChallenge(null);
    setError(null);
  }

  async function onPasskeySignIn() {
    setError(null);
    setPending(true);
    try {
      await finish(completeLogin(await signInWithPasskey(email)));
    } catch (err) {
      // A cancelled ceremony resets quietly; a real failure surfaces inline.
      const message = describePasskeyError(err);
      if (message) setError(message);
    } finally {
      setPending(false);
    }
  }

  async function onSubmitCredentials(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const result = await login(email, password);
      if (result.kind === "challenge") {
        setChallenge(result.challenge);
      } else {
        await finish(completeLogin(result.tokens));
      }
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  async function onSubmitNewPassword(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setError(null);
    setPending(true);
    try {
      const tokens = await setPassword({
        username: challenge.username,
        session: challenge.session,
        newPassword,
      });
      await finish(completeLogin(tokens));
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  async function onSubmitMfa(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setError(null);
    setPending(true);
    try {
      const tokens = await completeMfa({
        username: challenge.username,
        session: challenge.session,
        code: mfaCode,
      });
      await finish(completeLogin(tokens));
    } catch (err) {
      // 401 = the challenge session expired (not a bad code, which is 400) —
      // retrying a code can't revive a dead session, so drop back to the method
      // step with the message so the broker can start over.
      if (err instanceof ApiError && err.status === 401) {
        setChallenge(null);
        setMfaCode("");
      }
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  if (phase === "email-otp") {
    return (
      <EmailOtpLogin
        initialEmail={email}
        // Single-factor account: reuse the code /begin already sent. Method-step
        // entry (multi-factor): send on mount.
        presentSession={emailOtpPreSent ? beginSession : undefined}
        autoStart={!emailOtpPreSent}
        onAuthenticated={(tokens) => finish(completeLogin(tokens))}
        onUsePassword={() => {
          setPhase("method");
          setError(null);
        }}
      />
    );
  }

  if (challenge?.challenge === "SOFTWARE_TOKEN_MFA") {
    return (
      <AuthLayout
        title="Enter your authenticator code"
        description="Open your authenticator app and enter the current 6-digit code."
      >
        <form className="flex flex-col gap-4" onSubmit={onSubmitMfa}>
          <TextField
            label="Authenticator code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            required
          />
          {error && (
            <p role="alert" className="text-sm text-brk-danger">
              {error}
            </p>
          )}
          <p className="text-xs text-brk-dim">
            Codes refresh every 30 seconds. If one is rejected, enter the next
            one — and make sure your authenticator app’s clock is synced.
          </p>
          <SubmitButton full pending={pending} pendingLabel="Verifying…">
            Verify and sign in
          </SubmitButton>
        </form>
      </AuthLayout>
    );
  }

  if (challenge?.challenge === "NEW_PASSWORD_REQUIRED") {
    return (
      <AuthLayout
        title="Set a new password"
        description="Your account needs a new password before you can sign in."
      >
        <form className="flex flex-col gap-4" onSubmit={onSubmitNewPassword}>
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <PasswordRequirements value={newPassword} />
          <TextField
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          {error && (
            <p role="alert" className="text-sm text-brk-danger">
              {error}
            </p>
          )}
          <SubmitButton full pending={pending} pendingLabel="Saving…">
            Set password and sign in
          </SubmitButton>
        </form>
      </AuthLayout>
    );
  }

  const forgotFooter = (
    <>
      Forgot your password?{" "}
      <Link to="/forgot-password" className="text-brk-accent hover:underline">
        Reset it
      </Link>
    </>
  );

  // Step 1 — email only. The method affordances appear after the probe.
  if (phase === "email") {
    return (
      <AuthLayout
        title="Broker sign-in"
        description="Sign in to enter submissions and track your policies."
        footer={forgotFooter}
      >
        {notice && (
          <p
            role="status"
            className="text-center text-sm text-green-700 dark:text-green-300"
          >
            {notice}
          </p>
        )}
        <form className="flex flex-col gap-4" onSubmit={onSubmitEmail}>
          <TextField
            label="Email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {error && (
            <p role="alert" className="text-sm text-brk-danger">
              {error}
            </p>
          )}
          <SubmitButton full pending={pending} pendingLabel="Checking…">
            Continue
          </SubmitButton>
        </form>
        {DevLoginButton && (
          <Suspense fallback={null}>
            <DevLoginButton />
          </Suspense>
        )}
      </AuthLayout>
    );
  }

  // Step 2 — only the methods this account can actually use.
  return (
    <AuthLayout
      title="Broker sign-in"
      description="Sign in to enter submissions and track your policies."
      footer={forgotFooter}
    >
      <p className="text-sm text-brk-dim">
        Signing in as <span className="font-medium text-brk-text">{email}</span>{" "}
        ·{" "}
        <button
          type="button"
          onClick={useDifferentEmail}
          className="text-brk-accent hover:underline"
        >
          Use a different email
        </button>
      </p>

      {/* One alert for the step; the BFF message names the method that failed. */}
      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}

      {methods.includes("password") && (
        <form className="flex flex-col gap-4" onSubmit={onSubmitCredentials}>
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPasswordValue(e.target.value)}
            required
          />
          <SubmitButton full pending={pending} pendingLabel="Signing in…">
            Sign in
          </SubmitButton>
        </form>
      )}

      {methods.includes("password") &&
        (methods.includes("email_otp") || methods.includes("passkey")) && (
          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-brk-border" />
            <span className="text-xs uppercase tracking-wide text-brk-dim">
              or
            </span>
            <span className="h-px flex-1 bg-brk-border" />
          </div>
        )}

      {methods.includes("email_otp") && (
        <button
          type="button"
          onClick={() => {
            // A deliberate pick from the method step sends a fresh code.
            setEmailOtpPreSent(false);
            setPhase("email-otp");
            setError(null);
          }}
          className={altMethodClass}
        >
          Email me a sign-in code
        </button>
      )}

      {methods.includes("passkey") && (
        <button
          type="button"
          onClick={onPasskeySignIn}
          disabled={pending}
          className={altMethodClass}
        >
          Use a passkey
        </button>
      )}

      {methods.length === 0 && (
        <p className="text-sm text-brk-dim">
          No sign-in methods are available for this account. Contact your
          administrator.
        </p>
      )}
    </AuthLayout>
  );
}
